import { useEffect, useState } from 'react';

/**
 * VersionChecker component that periodically checks for app updates
 * and prompts the user to refresh when a new version is detected
 */
function VersionChecker() {
  const [showUpdate, setShowUpdate] = useState(false);
  const CHECK_INTERVAL = 60000; // Check every 60 seconds

  useEffect(() => {
    // Get the current version from the HTML meta tag or use build timestamp
    const getCurrentVersion = () => {
      const metaVersion = document.querySelector('meta[name="app-version"]');
      return metaVersion ? metaVersion.getAttribute('content') : null;
    };

    // Store the initial version
    const initialVersion = getCurrentVersion();
    if (initialVersion) {
      localStorage.setItem('appVersion', initialVersion);
    }

    // Function to check for updates
    const checkForUpdate = async () => {
      try {
        // Fetch the index.html to get the latest version
        const response = await fetch('/index.html', { 
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.ok) {
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const metaVersion = doc.querySelector('meta[name="app-version"]');
          const latestVersion = metaVersion ? metaVersion.getAttribute('content') : null;
          
          const storedVersion = localStorage.getItem('appVersion');
          
          // If versions don't match, show update notification
          if (latestVersion && latestVersion !== storedVersion) {
            setShowUpdate(true);
            // Update stored version
            localStorage.setItem('appVersion', latestVersion);
          }
        }
      } catch (error) {
        // Silently fail - don't interrupt user experience
        console.debug('Version check failed:', error);
      }
    };

    // Check immediately on mount
    checkForUpdate();

    // Set up periodic checking
    const interval = setInterval(checkForUpdate, CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  if (!showUpdate) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[200] bg-blue-600 text-white px-6 py-4 rounded-lg shadow-lg max-w-sm">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <p className="font-semibold text-sm">New version available!</p>
          <p className="text-xs mt-1 opacity-90">Refresh to get the latest updates</p>
        </div>
        <button
          onClick={handleRefresh}
          className="bg-white text-blue-600 px-4 py-2 rounded font-semibold text-sm hover:bg-blue-50 transition-colors whitespace-nowrap"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

export default VersionChecker;

